import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Eye, EyeOff, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '@/context/LanguageContext';

const RegisterPage = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error(t('common.fill_required')); return; }
    if (form.password.length < 6) { toast.error(t('auth.min6')); return; }
    setLoading(true);
    try {
      await register(form.name, form.email, form.password, form.phone, lang);
      toast.success(t('auth.created'));
      navigate(redirect ? `/${redirect}` : '/dashboard');
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === 'string' ? t(err.response.data.detail) : t('auth.register_fail'));
    }
    setLoading(false);
  };

  return (
    <div className="pt-20 min-h-screen flex items-center" data-testid="register-page">
      <div className="max-w-md mx-auto px-6 py-24 w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-10">
            <Crown className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="text-3xl font-['Cormorant_Garamond'] font-bold text-[#F5F2EB] mb-2">{t('auth.join')}</h1>
            <p className="text-sm text-[#A3A19C]">{t('auth.join_sub')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block text-sm text-[#A3A19C] mb-2">{t('auth.full_name')} *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                data-testid="register-name"
                className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                placeholder={t('auth.ph_full_name')}
              />
            </div>
            <div>
              <label className="block text-sm text-[#A3A19C] mb-2">{t('common.email')} *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm({ ...form, email: e.target.value })}
                data-testid="register-email"
                className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                placeholder="your@email.com" dir="ltr"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A3A19C] mb-2">{t('common.phone')}</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
                data-testid="register-phone"
                className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                placeholder="+973 XXXX XXXX" dir="ltr"
              />
            </div>
            <div>
              <label className="block text-sm text-[#A3A19C] mb-2">{t('common.password')} *</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  data-testid="register-password"
                  className="w-full bg-[#121215] border border-[rgba(255,255,255,0.1)] rounded-lg px-4 py-3 pe-12 text-[#F5F2EB] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors duration-300"
                  placeholder={t('auth.ph_min6')}
                />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute end-3 top-1/2 -translate-y-1/2 text-[#A3A19C]">
                  {showPass ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full btn-primary" data-testid="register-submit">
              {loading ? t('auth.creating') : t('auth.create')}
            </button>
          </form>

          <p className="text-center text-sm text-[#A3A19C] mt-8">
            {t('auth.have_account')} <Link to={redirect ? `/login?redirect=${redirect}` : "/login"} className="text-[#D4AF37] hover:text-[#E5C07B] transition-colors duration-300" data-testid="register-to-login">{t('auth.signin')}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
